import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import {IconArrowLeft, IconCloudDownload} from '@tabler/icons-react';
import React, {useEffect, useRef, useState} from 'react';
import type {
  PersonnelImportOptions,
  PersonnelImportPreviewResponse,
} from 'src/api/ProactApiClient';
import {
  PersonnelImportBlankSupervisorEnum,
  PersonnelImportIssueSeverityEnum,
  PersonnelImportRowErrorEnum,
  PersonnelImportSupervisorNotFoundEnum,
  UserTypeEnum,
} from 'src/api/ProactApiClient';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {Button} from 'src/design/atoms/Button';
import {DialogButton} from 'src/design/molecules/DialogButton';
import {FullScreenModal} from 'src/design/molecules/FullScreenModal';
import {
  useImportPersonnelMutation,
  usePreviewPersonnelImportMutation,
} from 'src/redux/queries/personnel';
import {useAppSelector} from 'src/redux/hooks';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {INSTANCE_ID_ZF} from 'src/shared/constants/constants';
import {useAlert} from 'src/shared/hooks/useAlert';
import {ApiCallerDownloadFile} from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {ReceiptStep} from './components/ReceiptStep';
import {ReviewStep} from './components/ReviewStep';
import {UploadStep} from './components/UploadStep';

type ImportPhase = 'upload' | 'review' | 'receipt';

const DEFAULT_OPTIONS: PersonnelImportOptions = {
  blankSupervisor: PersonnelImportBlankSupervisorEnum.KeepExisting,
  supervisorNotFound: PersonnelImportSupervisorNotFoundEnum.KeepExisting,
  rowsWithErrors: PersonnelImportRowErrorEnum.SkipRows,
};

export interface PersonnelImportModalProps {
  show: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
  userType: UserTypeEnum;
  vendorId?: number;
}

export const PersonnelImportModal: React.FC<PersonnelImportModalProps> = ({
  show,
  onClose,
  onImportSuccess,
  userType,
  vendorId,
}) => {
  const {setAlert} = useAlert();
  const sessionUser = useAppSelector(state => state.session.session?.user);

  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [files, setFiles] = useState<IFormDocumentUploadFile[]>([]);
  const [options, setOptions] =
    useState<PersonnelImportOptions>(DEFAULT_OPTIONS);
  const [preview, setPreview] = useState<PersonnelImportPreviewResponse>();
  const [previewError, setPreviewError] = useState<string>();
  const previewSequence = useRef(0);
  const previewedOptions = useRef(DEFAULT_OPTIONS);

  const [previewImport, {isLoading: isPreviewing}] =
    usePreviewPersonnelImportMutation();
  const [importPersonnel, {isLoading: isImporting}] =
    useImportPersonnelMutation();

  useEffect(() => {
    if (!show) return;
    setPhase('upload');
    setFiles([]);
    setOptions(DEFAULT_OPTIONS);
    setPreview(undefined);
    setPreviewError(undefined);
    previewedOptions.current = DEFAULT_OPTIONS;
  }, [show]);

  const isZfEmployeeImport =
    sessionUser?.instanceId === INSTANCE_ID_ZF &&
    userType === UserTypeEnum.Employee;

  const key = files[0]?.encodedKey;

  const counts = preview?.counts;
  const importableCount = counts
    ? counts.create + counts.update + counts.deactivate
    : 0;
  const hasFileErrors = (preview?.fileIssues ?? []).some(
    issue => issue.severity === PersonnelImportIssueSeverityEnum.Error
  );
  const isDefaultOptions = (
    Object.keys(DEFAULT_OPTIONS) as (keyof PersonnelImportOptions)[]
  ).every(optionKey => options[optionKey] === DEFAULT_OPTIONS[optionKey]);

  // A failed re-preview restores the options behind the preview still on
  // screen, so the confirm can never submit options that were not previewed.
  const runPreview = async (
    nextOptions: PersonnelImportOptions,
    isOptionChange = false
  ) => {
    if (!key) return;
    previewSequence.current += 1;
    const sequence = previewSequence.current;
    setPreviewError(undefined);
    try {
      const result = await previewImport({
        key,
        userType,
        vendorId,
        options: nextOptions,
      }).unwrap();
      // Overlapping option changes: only the newest request may land.
      if (sequence !== previewSequence.current) return;
      previewedOptions.current = nextOptions;
      setPreview(result);
    } catch (error) {
      if (sequence !== previewSequence.current) return;
      if (isOptionChange && preview) {
        setOptions(previewedOptions.current);
        setAlert(parseErrorMessage(error), 'error');
      } else {
        setPreview(undefined);
        setPreviewError(parseErrorMessage(error));
      }
    }
    setPhase('review');
  };

  const handleOptionsChange = (nextOptions: PersonnelImportOptions) => {
    setOptions(nextOptions);
    void runPreview(nextOptions, true);
  };

  const handleImport = async () => {
    if (!key) return;
    try {
      await importPersonnel({
        key,
        userType,
        vendorId,
        options: previewedOptions.current,
      }).unwrap();
      setPhase('receipt');
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleDownloadImportTemplate = () => {
    ApiCallerDownloadFile(
      ApiEndpoints.personnelImportTemplate,
      'personnel_import_template.csv'
    ).catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
  };

  const handleDone = () => {
    onClose();
    onImportSuccess();
  };

  const renderActions = () => {
    if (phase === 'receipt') {
      return (
        <Group justify="end">
          <Button onClick={handleDone}>
            Done
          </Button>
        </Group>
      );
    }

    if (phase === 'review') {
      const blockedReason = hasFileErrors
        ? 'Fix the problems listed above and upload the file again.'
        : importableCount === 0
          ? 'No rows can be imported. Upload a corrected file.'
          : undefined;

      return (
        <Group justify="space-between" align="center">
          <Group gap="md" align="center">
            <Button
              variant="subtle"
              color="black"
              size="sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setPhase('upload')}
              disabled={isImporting}>
              Upload a different file
            </Button>
            {blockedReason && (
              <Text size="sm" c="dimmed">
                {blockedReason}
              </Text>
            )}
          </Group>
          <Group justify="end">
            <DialogButton onClick={onClose}>Cancel</DialogButton>
            {!blockedReason && (
              <Button
                onClick={() => void handleImport()}
                loading={isImporting}
                disabled={isPreviewing}>
                {`Import ${importableCount} ${
                  importableCount === 1 ? 'row' : 'rows'
                }`}
              </Button>
            )}
          </Group>
        </Group>
      );
    }

    return (
      <Group justify="end">
        {!isZfEmployeeImport && (
          <DialogButton
            onClick={handleDownloadImportTemplate}
            leftSection={<IconCloudDownload />}>
            Download Template
          </DialogButton>
        )}
        <DialogButton onClick={onClose}>Cancel</DialogButton>
        <Button
          onClick={() => void runPreview(options)}
          loading={isPreviewing}
          disabled={!key}>
          Review file
        </Button>
      </Group>
    );
  };

  return (
    <FullScreenModal
      title="Import Personnel"
      open={show}
      onCloseCallback={phase === 'receipt' ? handleDone : onClose}
      fillHeight
      closeDisabled={isImporting}
      footer={
        <>
          <Divider mb="md" />
          {renderActions()}
        </>
      }>
      <div style={{flex: 1, minHeight: 0, overflowY: 'auto'}}>
        {phase === 'upload' && (
          <UploadStep
            files={files}
            isZfEmployeeImport={isZfEmployeeImport}
            onFilesChange={setFiles}
          />
        )}

        {phase === 'review' && (
          <ReviewStep
            preview={preview}
            isLoading={isPreviewing}
            error={previewError}
            options={options}
            onOptionsChange={handleOptionsChange}
            isDefaultOptions={isDefaultOptions}
          />
        )}

        {phase === 'receipt' && counts && (
          <ReceiptStep counts={counts} email={sessionUser?.email} />
        )}
      </div>
    </FullScreenModal>
  );
};
